/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Modal, Form as BootstrapForm, InputGroup, Image } from "react-bootstrap";
import {
  useCreateAccountMutation,
  useLoginMutation,
  useVerifyOTPMutation,
  useVerifyRegisterOTPMutation,
} from "../../../redux/api/user/AuthApi";
import OtpInput from "../../../components/OtpInput";
import { Link } from "react-router-dom";
import { setLocalStorage } from "../../../constants/localStorage";
import { getAuthDetails } from "../../../constants/helpers";
import { useGetBranchesQuery } from "../../../redux/api/Branches/BranchesApi";
import logo from "../../../assets/images/logo1.svg";
import "./Authentication.css";

const phoneValidation = Yup.string()
  .required("Phone number is required")
  .matches(/^[6-9][0-9]{9}$/, "Invalid phone number format");

const AccountCreationSchema = Yup.object().shape({
  designation: Yup.string()
    .oneOf(["Mr", "Miss", "Mrs", "Dr"], "Invalid Designation selection")
    .required("Designation is required"),
  branch: Yup.string().required("Branch is required"),
  phoneNumber: phoneValidation,
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  fullName: Yup.string()
    .required("Full name is required")
    .min(2, "Full name must be at least 2 characters"),
  dob: Yup.date()
    .required("Date of birth is required")
    .max(new Date(), "Date of birth cannot be in the future"),
  gender: Yup.string()
    .oneOf(["Male", "Female", "Other"], "Invalid gender selection")
    .required("Gender is required"),
  city: Yup.string(),
  area: Yup.string(),
});

const Authentication = ({
  onClose,
  formMode = "login",
  regTitle = "Registration",
}) => {
  const { mobileNumber } = getAuthDetails();
  const [authMode, setAuthMode] = useState(formMode);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [registrationData, setRegistrationData] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);

  // RTK Query mutations
  const [loginUser, { isLoading: isLoginLoading, error: loginError }] =
    useLoginMutation();
  const { data: branchesData, isLoading: branchLoader } = useGetBranchesQuery({
    page: "",
  });
  const [
    createAccount,
    { isLoading: isRegisterLoading, error: registerError },
  ] = useCreateAccountMutation();
  const [verifyOTP, { isLoading: isVerifyOtpLoading, error: verifyOtpError }] =
    useVerifyOTPMutation();
  const [
    verifyRegisterOTP,
    { isLoading: isVerifyRegisterOtpLoading, error: verifyRegisterOtpError },
  ] = useVerifyRegisterOTPMutation();

  // Timer for OTP resend
  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 0) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpTimer]);

  const startOtpTimer = () => {
    setOtpTimer(59);
  };

  useEffect(() => {
    if (branchesData && branchesData.success && branchesData.data) {
      setBranchOptions(branchesData.data);
    }
  }, [branchesData]);
  const handleLogin = async (values) => {
    try {
      const response = await loginUser({ mobile: values.phoneNumber }).unwrap();

      if (response.success) {
        setPhoneNumber(values.phoneNumber);
        setAuthMode("otpVerify");
        startOtpTimer();
      } else if (response.registrationRequired) {
        setAuthMode("register");
      }
    } catch (error) {
      console.error("Login error", error);
    }
  };

  const handleRegistration = async (values) => {
    try {
      const response = await createAccount({
        designation: values.designation,
        mobile: values.phoneNumber,
        fullName: values.fullName,
        email: values.email,
        dob: values.dob,
        gender: values.gender,
        city: values.city,
        area: values.area,
        registrationSource: "WEBSITE",
        orgId: values.branch,
      }).unwrap();

      if (response.success) {
        setRegistrationData({
          designation: values.designation,
          phoneNumber: values.phoneNumber,
          fullName: values.fullName,
          email: values.email,
          dob: values.dob,
          gender: values.gender,
          city: values.city,
          area: values.area,
          orgId: values.branch,
          registrationSource: "WEBSITE",
        });
        setPhoneNumber(values.phoneNumber);
        setAuthMode("registerOtpVerify");
        startOtpTimer();
      }
    } catch (error) {
      console.error("Registration error", error);
    }
  };

  const handleLoginOtpVerification = async () => {
    try {
      const requestBody = {
        mobile: phoneNumber,
        otp: otp,
      };

      const response = await verifyOTP(requestBody).unwrap();

      if (response.success) {
        localStorage.setItem("authToken", response?.token);
        setLocalStorage("mobileNumber", phoneNumber);
        onClose();
        window.location.reload();
      }
    } catch (error) {
      console.error("OTP Verification error", error);
    }
  };

  const handleRegisterOtpVerification = async () => {
    try {
      const requestBody = {
        mobile: phoneNumber,
        otp: otp,
        fullName: registrationData.fullName,
        orgId: registrationData.orgId,
      };
      console.log(requestBody);
      console.log(registrationData);

      const response = await verifyRegisterOTP(requestBody).unwrap();

      if (response.success) {
        localStorage.setItem("authToken", response?.token);
        setLocalStorage("mobileNumber", phoneNumber);
        onClose();
        window.location.reload();
      }
    } catch (error) {
      console.error("Register OTP Verification error", error);
    }
  };

  const resendOtp = async () => {
    try {
      if (authMode === "otpVerify") {
        await handleLogin({ phoneNumber });
      } else if (authMode === "registerOtpVerify") {
        await handleRegistration(registrationData);
      }
    } catch (error) {
      console.error("Resend OTP error", error);
    }
  };

  const renderLoginForm = () => (
    <Formik
      initialValues={{ phoneNumber: mobileNumber || "" }}
      validationSchema={Yup.object().shape({
        phoneNumber: phoneValidation,
      })}
      onSubmit={handleLogin}
    >
      {({ errors, touched, values }) => (
        <Form>
          <BootstrapForm.Group>
            <BootstrapForm.Label>Phone Number</BootstrapForm.Label>
            <InputGroup>
              {/* <InputGroup.Text>+91</InputGroup.Text> */}
              <Field
                name="phoneNumber"
                type="tel"
                placeholder={"Enter your phone number"}
                disabled={!!mobileNumber}
                className={`form-control ${
                  errors.phoneNumber && touched.phoneNumber ? "is-invalid" : ""
                }`}
              />
            </InputGroup>
            <ErrorMessage
              name="phoneNumber"
              component="div"
              className="text-danger"
            />
          </BootstrapForm.Group>

          <button
            type="submit"
            className="mt-3 w-100 btn-purple py-2"
            disabled={isLoginLoading}
          >
            {isLoginLoading ? "Sending..." : "Continue"}
          </button>

          {loginError && (
            <div className="text-danger text-center mt-2">
              {loginError.data?.message || "Login failed"}
            </div>
          )}

          <div className="text-center mt-3">
            <Link onClick={() => setAuthMode("register")}>
              Create New Account
            </Link>
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderRegistrationForm = () => (
    <Formik
      initialValues={{
        designation: "",
        phoneNumber: mobileNumber || "",
        fullName: "",
        email: "",
        dob: "",
        gender: "",
        city: "",
        area: "",
      }}
      validationSchema={AccountCreationSchema}
      onSubmit={handleRegistration}
    >
      {({ errors, touched, values }) => (
        <Form className="px-1">
          <div className="row gx-2">
            <div className="col-12">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Designation</BootstrapForm.Label>
                <Field
                  as="select"
                  name="designation"
                  className={`form-control ${
                    errors.designation && touched.designation
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <option value="" disabled>
                    Select Designation
                  </option>
                  <option value="Mr">Mr</option>
                  <option value="Miss">Miss</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Dr">Dr</option>
                </Field>
                <ErrorMessage
                  name="designation"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>
            <div className="col-12">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Full Name</BootstrapForm.Label>
                <Field
                  name="fullName"
                  type="text"
                  placeholder={"Enter your full name"}
                  className={`form-control ${
                    errors.fullName && touched.fullName ? "is-invalid" : ""
                  }`}
                />
                <ErrorMessage
                  name="fullName"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>
            <div className="col-12">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Phone Number</BootstrapForm.Label>
                <InputGroup>
                  {/* <InputGroup.Text className="px-2">+91</InputGroup.Text> */}
                  <Field
                    name="phoneNumber"
                    type="tel"
                    placeholder={"Enter your phone number"}
                    disabled={!!mobileNumber}
                    className={`form-control ${
                      errors.phoneNumber && touched.phoneNumber
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                </InputGroup>
                <ErrorMessage
                  name="phoneNumber"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>

            <div className="col-12">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Email</BootstrapForm.Label>
                <Field
                  name="email"
                  type="email"
                  placeholder={"Enter your email id"}
                  className={`form-control ${
                    errors.email && touched.email ? "is-invalid" : ""
                  }`}
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>
            <div className="col-12">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Choose Branch</BootstrapForm.Label>
                <Field
                  as="select"
                  name="branch"
                  className={`form-control ${
                    errors.branch && touched.branch ? "is-invalid" : ""
                  }`}
                >
                  <option value="" disabled selected>
                    Select Branch
                  </option>
                  {branchLoader ? (
                    <option disabled>Loading branches...</option>
                  ) : (
                    branchOptions.map((branch) => (
                      <option key={branch._id} value={branch?.orgId}>
                        {branch.branchName}
                      </option>
                    ))
                  )}
                </Field>
                <ErrorMessage
                  name="branch"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>
            <div className="col-md-6">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Date of Birth</BootstrapForm.Label>
                <Field
                  name="dob"
                  type="date"
                  className={`form-control ${
                    errors.dob && touched.dob ? "is-invalid" : ""
                  }`}
                />
                <ErrorMessage
                  name="dob"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>

            <div className="col-md-6">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Gender</BootstrapForm.Label>
                <Field
                  as="select"
                  name="gender"
                  className={`form-control ${
                    errors.gender && touched.gender ? "is-invalid" : ""
                  }`}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Field>
                <ErrorMessage
                  name="gender"
                  component="div"
                  className="text-danger small"
                />
              </BootstrapForm.Group>
            </div>

            <div className="col-md-6">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Area</BootstrapForm.Label>
                <Field
                  name="area"
                  type="text"
                  placeholder={"Enter your area"}
                  className="form-control"
                />
              </BootstrapForm.Group>
            </div>
            <div className="col-md-6">
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>City</BootstrapForm.Label>
                <Field
                  name="city"
                  type="text"
                  placeholder={"Enter your city"}
                  className="form-control"
                />
              </BootstrapForm.Group>
            </div>
          </div>

          <button
            type="submit"
            className="mt-2 w-100 btn-purple py-2"
            disabled={isRegisterLoading}
          >
            {isRegisterLoading ? "Registering..." : "Register"}
          </button>

          {registerError && (
            <div className="text-danger text-center mt-2 small">
              {registerError.data?.message || "Registration failed"}
            </div>
          )}

          {!mobileNumber && (
            <div className="text-center mt-3">
              <Link onClick={() => setAuthMode("login")}>
                Already have an account? Login
              </Link>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );

  const renderLoginOtpVerification = () => (
    <div>
      <BootstrapForm.Group>
        <BootstrapForm.Label>Enter OTP</BootstrapForm.Label>
        <OtpInput
          numInputs={6}
          value={otp}
          onChange={(newOtp) => setOtp(newOtp)}
        />
      </BootstrapForm.Group>

      <div className="text-center mt-3">
        {otpTimer > 0 ? (
          <p>Resend OTP in {otpTimer} seconds</p>
        ) : (
          <a
            className=" py-2 px-2 pointer"
            onClick={() => {
              resendOtp();
              setOtp("");
            }}
          >
            Resend OTP
          </a>
        )}
      </div>

      <button
        onClick={handleLoginOtpVerification}
        className="mt-3 w-100 btn-purple py-2"
        disabled={otp.length !== 6 || isVerifyOtpLoading}
      >
        {isVerifyOtpLoading ? "Verifying..." : "Verify OTP"}
      </button>

      {verifyOtpError && (
        <div className="text-danger text-center mt-2">
          {verifyOtpError.data?.message || "OTP Verification failed"}
        </div>
      )}
    </div>
  );

  const renderRegisterOtpVerification = () => (
    <div>
      <BootstrapForm.Group>
        <BootstrapForm.Label>Enter OTP</BootstrapForm.Label>
        <OtpInput
          numInputs={6}
          value={otp}
          onChange={(newOtp) => setOtp(newOtp)}
        />
      </BootstrapForm.Group>

      <div className="text-center mt-3">
        {otpTimer > 0 ? (
          <p>Resend OTP in {otpTimer} seconds</p>
        ) : (
          <a
            className=" py-2 px-2 pointer"
            onClick={() => {
              resendOtp();
              setOtp("");
            }}
          >
            Resend OTP
          </a>
        )}
      </div>

      <button
        onClick={handleRegisterOtpVerification}
        className="mt-3 w-100 btn-purple py-2"
        disabled={otp.length !== 6 || isVerifyRegisterOtpLoading}
      >
        {isVerifyRegisterOtpLoading ? "Verifying..." : "Verify OTP"}
      </button>

      {verifyRegisterOtpError && (
        <div className="text-danger text-center mt-2">
          {verifyRegisterOtpError.data?.message || "OTP Verification failed"}
        </div>
      )}
    </div>
  );

  return (
    <Modal show={true} centered onHide={onClose} backdrop="static" className="premium-auth-modal">
      <Modal.Header
        closeButton
        className="border-0 pb-0"
        closeVariant="dark"
      >
      </Modal.Header>
      <Modal.Body className="px-4 pb-5">
        <div className="text-center mb-4">
          <Image src={logo} height="40" className="mb-3" />
          <h2 className="auth-title">
            {authMode === "login" && "Welcome Back"}
            {authMode === "register" && (regTitle || "Book Appointment")}
            {authMode === "otpVerify" && "Verify OTP"}
            {authMode === "registerOtpVerify" && "Verify Registration"}
          </h2>
          <p className="text-muted">
            {authMode === "login" && "Enter your phone number to login"}
            {authMode === "register" && "Complete your details to continue"}
            {authMode === "otpVerify" && "Enter the 6-digit code sent to your phone"}
            {authMode === "registerOtpVerify" && "Enter the 6-digit code to complete registration"}
          </p>
        </div>
        
        <div className="auth-form">
          {authMode === "login" && renderLoginForm()}
          {authMode === "register" && renderRegistrationForm()}
          {authMode === "otpVerify" && renderLoginOtpVerification()}
          {authMode === "registerOtpVerify" && renderRegisterOtpVerification()}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default Authentication;
